import { Routes } from '@angular/router';
import { AppLayoutComponent } from './layout/app-layout/app-layout.component';
import { authGuard, guestGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'server-error',
    loadComponent: () => import('./pages/server-error/server-error.component').then(m => m.ServerErrorComponent)
  },
  {
    path: 'onboarding',
    loadComponent: () => import('./features/security/pages/onboarding-wizard-form/onboarding-wizard-form.component').then(m => m.OnboardingWizardFormComponent),
    canActivate: [authGuard, permissionGuard],
    data: { requiredAction: 'canView' }
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
    canActivate: [authGuard]
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'login'
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./features/dashboard/dashboard.routes').then(m => m.dashboardRoutes)
      },
      {
        path: 'account-settings',
        canActivate: [authGuard],
        loadComponent: () => import('./features/security/pages/account-settings/account-settings.component').then(m => m.AccountSettingsComponent),
        data: { title: 'Account Settings', icon: 'pi pi-cog', description: 'Manage your security credentials and profile settings.' }
      },
      {
        path: 'security',
        loadChildren: () => import('./features/security/security.routes').then(m => m.securityRoutes)
      },
      {
        path: 'administration',
        loadComponent: () => import('./shared/components/placeholder/placeholder.component').then(m => m.PlaceholderPageComponent),
        data: { title: 'Administration', icon: 'pi pi-sliders-h', description: 'Configure ERP parameters, email servers, and global workspace defaults.' }
      },
      {
        path: 'organizations',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/organization-list/organization-list.component').then(m => m.OrganizationListComponent),
        data: { title: 'Organizations', icon: 'pi pi-sitemap', description: 'Manage and configure your business organizations and hierarchy.', requiredAction: 'canView' }
      },
      {
        path: 'organizations/new',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/organization-form/organization-form.component').then(m => m.OrganizationFormComponent),
        data: { title: 'Create Organization', icon: 'pi pi-sitemap', description: 'Register a new organization on the ERP platform.', requiredAction: 'canCreate' }
      },
      {
        path: 'organizations/edit/:id',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/organization-form/organization-form.component').then(m => m.OrganizationFormComponent),
        data: { title: 'Edit Organization', icon: 'pi pi-sitemap', description: 'Modify organization configurations.', requiredAction: 'canEdit' }
      },
      {
        path: 'locations',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/masters/pages/location-form/location-form.component').then(m => m.LocationFormComponent),
        data: { title: 'Locations', icon: 'pi pi-map-marker', description: 'Administer physical and functional locations inside organizations.', requiredAction: 'canView' }
      },
      {
        path: 'location-types',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/masters/pages/location-type-form/location-type-form.component').then(m => m.LocationTypeFormComponent),
        data: { title: 'Location Types', icon: 'pi pi-tags', description: 'Administer classification categories for locations.', requiredAction: 'canView' }
      },
      {
        path: 'financial-years',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/masters/pages/financial-year-form/financial-year-form.component').then(m => m.FinancialYearFormComponent),
        data: { title: 'Financial Year Master', icon: 'pi pi-calendar', description: 'Administer financial years, fiscal periods, and transaction permissions.', requiredAction: 'canView' }
      },
      {
        path: 'series-masters',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/masters/pages/series-master-list/series-master-list.component').then(m => m.SeriesMasterListComponent),
        data: { title: 'Series Master', icon: 'pi pi-hashtag', description: 'Administer document numbering series, auto-increment formats, and default series assignments.', requiredAction: 'canView' }
      },
      {
        path: 'series-masters/new',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/masters/pages/series-master-form/series-master-form.component').then(m => m.SeriesMasterFormComponent),
        data: { title: 'Create Series Master', icon: 'pi pi-hashtag', description: 'Register a new document numbering series.', requiredAction: 'canCreate' }
      },
      {
        path: 'series-masters/edit/:id',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/masters/pages/series-master-form/series-master-form.component').then(m => m.SeriesMasterFormComponent),
        data: { title: 'Edit Series Master', icon: 'pi pi-hashtag', description: 'Modify series master configurations.', requiredAction: 'canEdit' }
      },
      {
        path: 'organization-services',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/organization-services/organization-services.component').then(m => m.OrganizationServicesComponent),
        data: { title: 'Organization Services', icon: 'pi pi-briefcase', description: 'Configure active modules and custom service integrations per organization.', requiredAction: 'canView' }
      },
      {
        path: 'organization-service-modules',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/organization-service-module/organization-service-module.component').then(m => m.OrganizationServiceModuleComponent),
        data: { title: 'Organization Service Modules', icon: 'pi pi-th-large', description: 'Configure and assign active service modules per organization.', requiredAction: 'canView' }
      },
      {
        path: 'service-modules',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/service-module-form/service-module-form.component').then(m => m.ServiceModuleFormComponent),
        data: { title: 'Service Modules Management', icon: 'pi pi-th-large', description: 'Configure and assign active service modules per organization.', requiredAction: 'canView' }
      },
      {
        path: 'services',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/service-form/service-form.component').then(m => m.ServiceFormComponent),
        data: { title: 'System Services', icon: 'pi pi-cog', description: 'Administer system service codes and platform service features.', requiredAction: 'canView' }
      },
      {
        path: 'modules',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/security/pages/module-form/module-form.component').then(m => m.ModuleFormComponent),
        data: { title: 'System Modules', icon: 'pi pi-th-large', description: 'Administer system modules and service mappings.', requiredAction: 'canView' }
      },
      {
        path: 'item-categories',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/shared-business-services/pages/item-category-form/item-category-form.component').then(m => m.ItemCategoryFormComponent),
        data: { title: 'Item Category Master', icon: 'pi pi-tags', description: 'Manage item classification categories and parent-child category hierarchies across services.', requiredAction: 'canView' }
      },
      {
        path: 'item-brands',
        canActivate: [permissionGuard],
        loadComponent: () => import('./features/shared-business-services/pages/item-brand-form/item-brand-form.component').then(m => m.ItemBrandFormComponent),
        data: { title: 'Item Brand Master', icon: 'pi pi-tags', description: 'Manage item brand registries, brand codes, and organization mappings.', requiredAction: 'canView' }
      },
      {
        path: 'masters',
        loadComponent: () => import('./shared/components/placeholder/placeholder.component').then(m => m.PlaceholderPageComponent),
        data: { title: 'Masters Registry', icon: 'pi pi-database', description: 'Manage accounts, customers, vendors, and products.' }
      },
      {
        path: 'inventory',
        loadComponent: () => import('./shared/components/placeholder/placeholder.component').then(m => m.PlaceholderPageComponent),
        data: { title: 'Inventory Management', icon: 'pi pi-box', description: 'Track stock balances, warehouse transfers, and low items alerts.' }
      },
      {
        path: 'purchase',
        loadComponent: () => import('./shared/components/placeholder/placeholder.component').then(m => m.PlaceholderPageComponent),
        data: { title: 'Purchase Operations', icon: 'pi pi-shopping-cart', description: 'Create purchase requisitions, orders, and receive goods.' }
      },
      {
        path: 'sales',
        loadComponent: () => import('./shared/components/placeholder/placeholder.component').then(m => m.PlaceholderPageComponent),
        data: { title: 'Sales & Invoicing', icon: 'pi pi-receipt', description: 'Manage customer sales quotes, invoices, and payment receipts.' }
      },
      {
        path: 'workflow',
        loadComponent: () => import('./shared/components/placeholder/placeholder.component').then(m => m.PlaceholderPageComponent),
        data: { title: 'Workflow Approvals', icon: 'pi pi-sitemap', description: 'Define review stages, track pending actions, and check transaction logs.' }
      },
      {
        path: 'reports',
        loadComponent: () => import('./shared/components/placeholder/placeholder.component').then(m => m.PlaceholderPageComponent),
        data: { title: 'Business Reports', icon: 'pi pi-chart-line', description: 'Generate balance sheets, profit-loss reports, and visual charts.' }
      },
      {
        path: 'server-error',
        loadComponent: () => import('./pages/server-error/server-error.component').then(m => m.ServerErrorComponent),
        data: { title: 'Internal Server Error', icon: 'pi pi-exclamation-triangle', description: 'Internal server error status page.' }
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'login'
  }
];
